import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import useOutsideClick from 'utils/useOutsideClick';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import ThreeDotsIcon from 'components/atoms/icons/threeDotsIcon';
import { getSmsDashboardScheduledMessagesKey } from 'infrastructure/hooks/smsDashboard/useScheduledMessages';
import { useCancelScheduledMessage } from 'infrastructure/hooks/smsDashboard/useCancelScheduledMessage';
import ChevronLeftIcon from 'components/atoms/icons/chevronLeft';
import { SmsDashboardTabEnums } from 'infrastructure/enums';

import s from './styles.module.scss';
import { scrollMessagesToBottom } from '../messagesList';

import type { RootState } from 'store';
import type {
  IScheduledMessagesListRes,
  IConversation,
  IScheduledMessage,
} from 'infrastructure/interfaces';

interface IChatScheduledHeaderProps {
  conversation: IConversation;
  isScheduledMessagesShown: boolean;
  setIsScheduledMessagesShown: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChatScheduledHeader = (props: IChatScheduledHeaderProps) => {
  const {
    conversation,
    isScheduledMessagesShown,
    setIsScheduledMessagesShown,
  } = props;

  const ref = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const { currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  useOutsideClick(ref, () => {
    setIsMenuOpen(false);
  });

  const scheduledMessagesKey = getSmsDashboardScheduledMessagesKey({
    conversationId: currentConversation.id,
  });

  const scheduledMessages =
    queryClient.getQueryData<IScheduledMessagesListRes>(scheduledMessagesKey);

  const { cancelScheduledMessage } = useCancelScheduledMessage({
    conversationId: currentConversation.id,
    onSuccess: () => {
      notificationController.info({
        message: t('smsDashboard.scheduled_message_cancelled'),
        key: 'scheduled_message_cancelled',
      });
    },
  });
  const conversationsSortButtonClassNames = classNames(
    s['conversations-sort-button'],
    s['card-border'],
    {
      active: isMenuOpen,
    },
  );

  const onCancelScheduledMessage = () => {
    scheduledMessages?.items?.forEach(
      async (scheduledMessage: IScheduledMessage) => {
        await cancelScheduledMessage({
          conversationId: conversation.id,
          messageId: scheduledMessage.messageId,
        });
      },
    );

    scrollMessagesToBottom();
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className={s['chat-header']}>
      <button
        type="button"
        aria-label="Previous"
        className={s.prev}
        data-cy="pagination__prev"
        onClick={() => setIsScheduledMessagesShown(!isScheduledMessagesShown)}
      >
        <ChevronLeftIcon />
      </button>
      <div className={s['chat-header-info']}>
        <div className={s['chat-header-patient']}>
          <div className={s['patient-name']}>
            <span>{t('smsDashboard.scheduled_messages')}</span>
          </div>
        </div>
        <div className={s['chat-header-buttons']}>
          <div className={s['conversations-filters']}>
            <div className={s['conversations-sort']} ref={ref}>
              {!!scheduledMessages?.items?.length && (
                <Card className={conversationsSortButtonClassNames}>
                  <CardBody
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className={s['card-body-sort-arrow']}
                  >
                    <ThreeDotsIcon />
                  </CardBody>
                </Card>
              )}
              {isMenuOpen && (
                <div className={s['conversations-sort-body']}>
                  {conversation.type !==
                    SmsDashboardTabEnums.UNREGISTERED_RPM &&
                    conversation.type !==
                      SmsDashboardTabEnums.UNREGISTERED_CCM &&
                    scheduledMessages?.items?.length && (
                      <div
                        className={s['conversations-sort-item']}
                        onClick={onCancelScheduledMessage}
                      >
                        <span>{t(`smsDashboard.cancel_sending`)}</span>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatScheduledHeader;
